import { useRef, useState, useEffect } from "react";
import { useCountUp } from "@/hooks/useCountUp";

const caseStudies = [
  {
    company: "Crunchyroll",
    role: "Head of Marketing",
    metrics: ["45M+ users", "2M+ subscribers", "$1.2B acquisition"],
    description:
      "Scaled from niche streaming platform to the world's largest anime brand. Launched Crunchyroll Games, Expo, and The Anime Awards as multimillion-dollar revenue streams.",
  },
  {
    company: "Castle",
    role: "VP of Growth",
    metrics: ["2.2M+ MAU", "60% CPI reduction", "$15M seed round"],
    description:
      "Built the growth engine behind Castle's $15M seed round. Scaled MAU 60% to 2.2M+, cut acquisition costs 60%, improved D30 retention 32%.",
  },
  {
    company: "SmartDreams",
    role: "Co-Founder",
    metrics: ["Apple Top AI App 2023", "4.9★ rating", "25K+ stories"],
    description:
      "Co-founded AI-powered children's reading app. Named by Apple alongside ChatGPT, Canva, and Pinterest. 4.9-star App Store rating.",
  },
  {
    company: "Sketchy",
    role: "Head of Marketing",
    metrics: ["33% YoY revenue growth", "+20 NPS", "10-person team"],
    description:
      "Drove 33% YoY revenue growth through retention and lifecycle optimization. Led full rebrand. Built 10-person cross-functional growth team.",
  },
];

const AnimatedMetric = ({ metric, inView }: { metric: string; inView: boolean }) => {
  const display = useCountUp(metric, inView);
  return (
    <span className="font-mono text-lg md:text-xl font-semibold text-primary leading-tight">
      {display}
    </span>
  );
};

const ResultsCard = ({ study }: { study: typeof caseStudies[0] }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="group bg-card border border-border rounded-xl p-7 md:p-9 transition-all duration-300 hover:border-primary/40 hover:shadow-[0_0_24px_-6px_hsl(var(--primary)/0.15)] hover:-translate-y-0.5"
    >
      <div className="mb-6">
        <h3 className="font-body text-base font-semibold text-foreground">{study.company}</h3>
        <p className="font-body text-sm text-muted-foreground mt-0.5">{study.role}</p>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 mb-6">
        {study.metrics.map((metric) => (
          <AnimatedMetric key={metric} metric={metric} inView={inView} />
        ))}
      </div>
      <div className="h-px bg-border mb-5" />
      <p className="font-body text-sm text-muted-foreground leading-relaxed">{study.description}</p>
    </div>
  );
};

const ResultsSection = () => {
  return (
    <section id="results" className="section-padding bg-background">
      <div className="content-max">
        <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl text-foreground tracking-tight mb-12 md:mb-16">
          Proven Results at Scale
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {caseStudies.map((study) => (
            <ResultsCard key={study.company} study={study} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ResultsSection;
